import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { makeGraph, getDate, getFormat, twoDigits } from 'lib/commonLib';
import { requestMCGraph } from 'lib/api/request';

import CurrentStatus from 'components/atoms/CurrentStatus';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ContentsWrap from 'components/molecules/ContentsWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    data: { bar: ['REQ_CNT'], line: ['ENT_RATE'] },
    yAxisData: {
      left: { REQ_CNT: true },
      right: { ENT_RATE: true, isPercent: true },
    },
    customizeData: {
      ENT_RATE: {
        isPercent: true,
      },
    },
    referenceLine: {
      REQ_CNT: {
        type: 'average',
        label: i18next.t('요청수'),
        visibleValue: true,
      },
    },
  },
];

class McTargetStatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      noData: false,
      error: false,
    };
  }

  getData = () => {
    const { params, locale, t } = this.props;
    // const sDate = moment(moment().format('YYYY-MM-01'));
    // const eDate = moment().subtract(1, 'days');
    const format = getFormat(locale);
    const { sDate, eDate } = params;

    if (sDate === eDate && eDate === getDate(moment().subtract(1, 'days'))) {
      params['sDate'] = getDate(moment(moment().format('YYYY-MM-01')));
      params['eDate'] = getDate(moment().subtract(1, 'days'));
    }

    this.setState({
      ...this.state,
      loading: true,
      noData: false,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    requestMCGraph(params)
      .then((response) => {
        const { data } = response.data;
        if (data.length === 0) {
          this.setState({
            ...this.state,
            loading: false,
            noData: true,
            error: false,
          });
        } else {
          this.setState({
            ...this.state,
            primitiveData: data,
            loading: false,
            noData: false,
            error: false,
          });
        }

        this.handleChangeGraph(graphList[0], 0)();
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          noData: false,
          error: true,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t, theme } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  xAxisTick = (tickProps) => {
    const { theme } = this.props;
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = this.makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;

      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, referenceData } =
          makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            width="100%"
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  render() {
    const { handleRefesh } = this;
    const { t } = this.props;
    const { infoMessage, curGraphComponent, loading, noData, error } =
      this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('요청수 데이터 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || noData || error ? (
          <CurrentStatus
            type={loading ? 'loading' : noData ? 'noData' : error && 'error'}
          />
        ) : (
          <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
        )}
      </SwitchingArea>
    );
  }
}

export default withTranslation()(
  connect(
    ({ locale }) => ({
      locale: locale.locale,
    }),
    null
  )(withTheme(McTargetStatisticsGraph))
);
